import React from 'react';

interface DriftEvent {
  type: string;
  severity: string;
  field?: string;
  before?: string | number | null;
  after?: string | number | null;
  description?: string;
}

interface Props {
  events: DriftEvent[];
  fromVersion?: number;
  toVersion?: number;
}

const SEVERITY_COLORS: Record<string, { bg: string; border: string; text: string; glow: string }> = {
  critical: { bg: '#ef444420', border: '#ef4444', text: '#fca5a5', glow: '#ef444440' },
  high: { bg: '#f9731620', border: '#f97316', text: '#fdba74', glow: '#f9731640' },
  medium: { bg: '#eab30820', border: '#eab308', text: '#fde047', glow: '#eab30840' },
  low: { bg: '#22c55e20', border: '#22c55e', text: '#86efac', glow: '#22c55e40' },
};

const TYPE_LABELS: Record<string, { icon: string; label: string }> = {
  tone_shift: { icon: '🎭', label: 'Tone Shift' },
  boundary_removed: { icon: '🚨', label: 'Boundary Removed' },
  boundary_weakened: { icon: '⚠️', label: 'Boundary Weakened' },
  autonomy_escalation: { icon: '🔺', label: 'Autonomy Escalation' },
  autonomy_reduction: { icon: '🔽', label: 'Autonomy Reduction' },
  threshold_increase: { icon: '📈', label: 'Threshold Changed' },
  identity_change: { icon: '🪞', label: 'Identity Change' },
  node_added: { icon: '➕', label: 'Node Added' },
  node_removed: { icon: '➖', label: 'Node Removed' },
  node_modified: { icon: '✏️', label: 'Node Modified' },
};

export const DriftHeatmap: React.FC<Props> = ({ events, fromVersion, toVersion }) => {
  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <span className="text-3xl mb-3">🧊</span>
        <h3 className="text-sm font-bold text-white mb-1">No Drift Detected</h3>
        <p className="text-xs text-slate-400 max-w-xs">
          This Replica's cognitive profile is stable. Drift events will appear here
          when profile changes are significant.
        </p>
      </div>
    );
  }

  // Group by severity
  const bySeverity = events.reduce<Record<string, DriftEvent[]>>((acc, e) => {
    const s = e.severity || 'low';
    if (!acc[s]) acc[s] = [];
    acc[s].push(e);
    return acc;
  }, {});

  const severityOrder = ['critical', 'high', 'medium', 'low'];
  const totalEvents = events.length;
  const criticalCount = bySeverity['critical']?.length || 0;
  const highCount = bySeverity['high']?.length || 0;

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white">Drift Monitor</h3>
          {fromVersion && toVersion && (
            <p className="text-xs text-slate-400">v{fromVersion} → v{toVersion}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">
              {criticalCount} critical
            </span>
          )}
          {highCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 font-medium">
              {highCount} high
            </span>
          )}
          <span className="text-xs text-slate-500">{totalEvents} total</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-10 gap-1">
        {events.map((event, i) => {
          const colors = SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.low;
          const typeInfo = TYPE_LABELS[event.type] || { icon: '📊', label: event.type };
          return (
            <div
              key={i}
              className="group relative"
            >
              <div
                className="w-full aspect-square rounded-md border transition-all hover:scale-125 hover:z-10 cursor-pointer"
                style={{
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  boxShadow: `0 0 8px ${colors.glow}`,
                }}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                <div className="bg-slate-900 border border-slate-600/50 rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm">{typeInfo.icon}</span>
                    <span className="text-xs font-medium text-white">{typeInfo.label}</span>
                  </div>
                  {event.description && (
                    <p className="text-xs text-slate-400 max-w-[200px] truncate">{event.description}</p>
                  )}
                  <span className="text-xs capitalize font-medium" style={{ color: colors.text }}>
                    {event.severity}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Severity Legend */}
      <div className="flex items-center gap-4 pt-2 border-t border-slate-700/30">
        {severityOrder.map(sev => {
          const colors = SEVERITY_COLORS[sev];
          const count = bySeverity[sev]?.length || 0;
          return (
            <div key={sev} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm border" style={{ backgroundColor: colors.bg, borderColor: colors.border }} />
              <span className="text-xs text-slate-400 capitalize">{sev} ({count})</span>
            </div>
          );
        })}
      </div>

      {/* Event List */}
      <div className="space-y-1.5">
        {severityOrder.map(sev => {
          const group = bySeverity[sev];
          if (!group || group.length === 0) return null;
          return group.map((event, i) => {
            const colors = SEVERITY_COLORS[sev];
            const typeInfo = TYPE_LABELS[event.type] || { icon: '📊', label: event.type };
            return (
              <div key={`${sev}-${i}`}
                className="flex items-start gap-3 px-3 py-2.5 rounded-xl border transition-colors"
                style={{ backgroundColor: colors.bg, borderColor: `${colors.border}30` }}>
                <span className="text-base mt-0.5">{typeInfo.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-bold" style={{ color: colors.text }}>{typeInfo.label}</span>
                    <span className="text-xs capitalize px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.border}20`, color: colors.text }}>
                      {sev}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-xs text-slate-300">{event.description}</p>
                  )}
                  {event.before && event.after && (
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className="text-red-400 line-through">{event.before}</span>
                      <span className="text-slate-500">→</span>
                      <span className="text-green-400">{event.after}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
};

export default DriftHeatmap;
